<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">

            <div class="modal-header">
                <h4 class="modal-title">
                    <i class="fa-solid" :class="editMode ? 'fa-edit' : 'fa-plus'"></i>
                    {{ editMode ? 'Edit Appointment Type' : 'Create Appointment Type' }}
                </h4>
                <button class="modal-close" @click="closeModal">
                    <i class="fa-solid fa-times"></i>
                </button>
            </div>

            <div class="modal-body">
                <div class="row g-3">

                    <div class="col-12">
                        <label class="form-label-theme">Label <span class="required">*</span></label>
                        <input v-model="form.label" type="text" class="form-control-theme"
                            :class="{ 'is-invalid': errors.label }" placeholder="e.g. New Patient Short Exam" />
                        <div v-if="errors.label" class="validation-error">
                            {{ errors.label }}
                        </div>
                    </div>

                    <div class="col-12">
                        <label class="form-label-theme">Description <span class="required">*</span></label>
                        <textarea v-model="form.description" class="form-control-theme" rows="3"
                            placeholder="Description..." :class="{ 'is-invalid': errors.description }"></textarea>
                        <div v-if="errors.description" class="validation-error">
                            {{ errors.description }}
                        </div>
                    </div>

                </div>
            </div>

            <div class="modal-footer">
                <button class="btn-theme-secondary" @click="closeModal">Cancel</button>

                <button v-if="editMode" class="btn-theme-outline-danger" @click="handleDelete">
                    <i class="fa-solid fa-trash me-2"></i> Delete
                </button>

                <button class="btn-theme-success" :disabled="saving" @click="handleSave">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    {{ editMode ? "Update" : "Create" }}
                </button>
            </div>

        </div>
    </div>
</template>


<script>
import { AppointmentTypeService } from "@/services/appointmentType.service";

export default {
    name: "AppointmentTypeModal",

    props: {
        show: Boolean,
        appointmentType: Object
    },

    emits: ["close", "saved", "deleted", "fetch-appointment-types"],

    data() {
        return {
            saving: false,
            form: {
                label: "",
                description: ""
            },
            errors: {
                label: "",
                description: ""
            }
        };
    },

    computed: {
        editMode() {
            return !!(this.appointmentType?._id || this.appointmentType?.id);
        }
    },

    watch: {
        show(val) {
            if (val) this.loadForm();
        },
        appointmentType() {
            this.loadForm();
        }
    },

    methods: {
        loadForm() {
            if (this.editMode) {
                this.form = {
                    label: this.appointmentType.label,
                    description: this.appointmentType.description || ""
                };
            } else {
                this.form = { label: "", description: "" };
            }
            this.errors.label = "";
            this.errors.description = "";
        },

        validate() {
            let isValid = true;

            // reset errors
            this.errors.label = "";
            this.errors.description = "";

            if (!this.form.label.trim()) {
                this.errors.label = "Label is required";
                isValid = false;
            }

            if (!this.form.description.trim()) {
                this.errors.description = "Description is required";
                isValid = false;
            }

            return isValid;
        },

        async handleSave() {
            if (!this.validate()) return;

            this.saving = true;
            try {
                const payload = {
                    label: this.form.label.trim(),
                    description: this.form.description.trim()
                };

                const typeId = this.appointmentType?._id || this.appointmentType?.id;
                const res = this.editMode
                    ? await AppointmentTypeService.update(typeId, payload)
                    : await AppointmentTypeService.create(payload);
                
                if (res.data.status === 200 || res.data.status === 201) {
                    this.$emit("saved", res.data.data);
                    this.$emit("fetch-appointment-types");
                    this.closeModal();
                    if (this.editMode) {
                        this.$store.commit("toast/NEW", {
                            message: res?.data?.data?.message || "Appointment type updated successfully",
                            type: "success"
                        });
                    } else {
                        this.$store.commit("toast/NEW", {
                            message: res?.data?.data?.message || "Appointment type created successfully",
                            type: "success"
                        });
                    }
                }
            } catch (error) {
                console.log(error);
                const res = error?.response?.data
                this.$store.commit("toast/NEW", {
                    message: res?.data?.message  || res?.data?.data?.message  || "Something went wrong",
                    type: "error"
                });
            } finally {
                this.saving = false;
            }
        },

        async handleDelete() {
            try {
                if (!confirm("Delete this appointment type?")) return;

                const typeId = this.appointmentType?._id || this.appointmentType?.id;
                const res = await AppointmentTypeService.delete(typeId);

                if (res.data.status === 200) {
                    this.$emit("deleted", typeId);
                    this.$emit("fetch-appointment-types");
                    this.closeModal();
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.data?.message || "Appointment type deleted successfully",
                        type: "success"
                    });
                }
            } catch (error) {
                const res = error?.response?.data
                this.$store.commit("toast/NEW", {
                    message: res?.data?.message  || res?.data?.data?.message  || "Something went wrong",
                    type: "error"
                });
            }
        },

        closeModal() {
            this.$emit("close");
        }
    }
};
</script>


<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

.modal-container {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 700px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease-out;
}

.modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8faff;
    border-radius: 16px 16px 0 0;
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #0144A6;
    display: flex;
    align-items: center;
    gap: 10px;
}

.modal-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    color: #666;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.2s;
}

.modal-close:hover {
    background: #eef2f6;
    color: #dc3545;
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

.form-container {
    padding: 10px;
}

.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #eef2f6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: #f8faff;
    border-radius: 0 0 16px 16px;
}

/* Form Styles */
.form-label-theme {
    font-weight: 600;
    margin-bottom: 6px;
    font-size: 14px;
    color: #444;
    display: block;
}

.form-control-theme,
.form-select-theme {
    width: 100%;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.2s;
}

.form-control-theme:focus,
.form-select-theme:focus {
    border-color: #3c78f9;
    outline: none;
    box-shadow: 0 0 0 3px rgba(60, 120, 249, 0.1);
}

.form-control-theme.is-invalid,
.form-select-theme.is-invalid {
    border-color: #dc3545;
}

.required {
    color: #dc3545;
    font-weight: bold;
}

.validation-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
    font-weight: 500;
    min-height: 18px;
}

/* Color Selection */
.color-selection {
    background: #f8faff;
    border: 1px solid #eef2f6;
    border-radius: 12px;
    padding: 20px;
}

.color-options {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 10px;
    margin-bottom: 20px;
}

.color-option {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
    border: 3px solid transparent;
    transition: all 0.2s;
}

.color-option:hover {
    transform: scale(1.1);
}

.color-option.selected {
    border-color: #333;
    box-shadow: 0 0 0 2px white, 0 0 0 4px currentColor;
}

.custom-color-input {
    display: flex;
    align-items: center;
    gap: 15px;
}

.form-control-color {
    width: 50px;
    height: 50px;
    padding: 0;
    border: 1px solid #ddd;
    border-radius: 8px;
    cursor: pointer;
}

.color-preview {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid #ddd;
}

.color-value {
    font-size: 14px;
    color: #666;
    font-family: monospace;
    background: white;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #ddd;
}

/* Button Styles */
.btn-theme-secondary {
    background: #f1f3f5;
    color: #333;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-secondary:hover {
    background: #e9ecef;
}

.btn-theme-success {
    background: #0144A6;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-success:hover:not(:disabled) {
    background: #013179;
}

.btn-theme-success:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-theme-outline-danger {
    background: transparent;
    border: 2px solid #dc3545;
    color: #dc3545;
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 600;
    transition: all 0.2s;
}

.btn-theme-outline-danger:hover {
    background: #dc3545;
    color: white;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .modal-container {
        width: 95%;
        max-height: 95vh;
    }

    .modal-body {
        padding: 16px;
    }

    .color-options {
        grid-template-columns: repeat(4, 1fr);
    }

    .modal-footer {
        flex-direction: column;
    }

    .modal-footer button {
        width: 100%;
    }

    .custom-color-input {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>